/**
 * Clinical abbreviation / synonym expansion for search.
 *
 * Residents type abbreviations ("mi", "dka", "sob"); the notes often spell the
 * term out ("myocardial infarction"). Each query token is expanded to the other
 * members of its concept group so the abbreviation and the full term find each
 * other. Expansion only *adds* recall (it's OR'd with the literal token) and is
 * combined with AND across concepts, so multi-word queries still narrow.
 *
 * Forward expansion (abbreviation → full term) is the common case and always
 * works. Reverse, multi-word phrases (typing the full term to match notes that
 * only ever abbreviate) is best-effort: add the spelled-out form as its own group
 * member when it matters. The map is intentionally easy to extend.
 */

// Each row is a set of clinically equivalent terms. Lower-case; entries may be
// single tokens (matched directly) or multi-word phrases (matched as AND of words).
const CONCEPT_GROUPS: string[][] = [
  // Cardiology
  ['mi', 'myocardial infarction'],
  ['nstemi', 'non st elevation myocardial infarction'],
  ['stemi', 'st elevation myocardial infarction'],
  ['acs', 'acute coronary syndrome'],
  ['afib', 'af', 'atrial fibrillation'],
  ['aflutter', 'atrial flutter'],
  ['rvr', 'rapid ventricular response'],
  ['svt', 'supraventricular tachycardia'],
  ['vt', 'ventricular tachycardia'],
  ['chf', 'hf', 'heart failure', 'congestive heart failure'],
  ['hfref', 'heart failure with reduced ejection fraction'],
  ['hfpef', 'heart failure with preserved ejection fraction'],
  ['htn', 'hypertension'],
  ['hld', 'hyperlipidemia'],
  ['cad', 'coronary artery disease'],
  ['pad', 'peripheral arterial disease'],
  ['dvt', 'deep vein thrombosis'],
  ['pe', 'pulmonary embolism'],
  ['vte', 'venous thromboembolism'],
  ['scape', 'flash pulmonary edema', 'sympathetic crashing acute pulmonary edema'],

  // Pulmonary
  ['copd', 'chronic obstructive pulmonary disease'],
  ['pna', 'cap', 'pneumonia', 'community acquired pneumonia'],
  ['hap', 'hospital acquired pneumonia'],
  ['ards', 'acute respiratory distress syndrome'],
  ['sob', 'shortness of breath', 'dyspnea'],
  ['osa', 'obstructive sleep apnea'],
  ['niv', 'nippv', 'noninvasive ventilation'],
  ['pft', 'pulmonary function test'],

  // Endocrine / metabolic
  ['dm', 'diabetes', 'diabetes mellitus'],
  ['t2dm', 'type 2 diabetes'],
  ['t1dm', 'type 1 diabetes'],
  ['dka', 'diabetic ketoacidosis'],
  ['hhs', 'hyperosmolar hyperglycemic state'],
  ['hypoglycemia', 'low blood sugar'],
  ['hyperk', 'hyperkalemia'],
  ['hypok', 'hypokalemia'],
  ['hypona', 'hyponatremia'],
  ['hyperna', 'hypernatremia'],
  ['tsh', 'thyroid'],

  // Renal
  ['aki', 'acute kidney injury'],
  ['ckd', 'chronic kidney disease'],
  ['esrd', 'end stage renal disease'],
  ['uti', 'urinary tract infection'],
  ['hd', 'hemodialysis'],

  // GI / hepatology
  ['gib', 'gi bleed', 'gastrointestinal bleed'],
  ['ugib', 'upper gi bleed'],
  ['lgib', 'lower gi bleed'],
  ['pud', 'peptic ulcer disease'],
  ['ibd', 'inflammatory bowel disease'],
  ['gerd', 'gastroesophageal reflux disease'],
  ['sbo', 'small bowel obstruction'],
  ['lft', 'liver function test'],
  ['cirrhosis', 'esld', 'end stage liver disease'],
  ['pancreatitis', 'acute pancreatitis'],

  // Neurology
  ['cva', 'stroke', 'cerebrovascular accident'],
  ['tia', 'transient ischemic attack'],
  ['sah', 'subarachnoid hemorrhage'],
  ['ich', 'intracerebral hemorrhage', 'intracranial hemorrhage'],
  ['ams', 'altered mental status', 'encephalopathy'],
  ['sz', 'seizure'],
  ['iih', 'idiopathic intracranial hypertension', 'pseudotumor cerebri'],

  // ID / sepsis
  ['sepsis', 'septic shock'],
  ['ssti', 'skin and soft tissue infection', 'cellulitis'],
  ['abx', 'antibiotics'],
  ['bcx', 'blood culture'],

  // Heme / onc
  ['vte ppx', 'dvt prophylaxis'],
  ['dic', 'disseminated intravascular coagulation'],
  ['hit', 'heparin induced thrombocytopenia'],

  // General / orders
  ['ppx', 'prophylaxis'],
  ['ppi', 'proton pump inhibitor'],
  ['ivf', 'intravenous fluids'],
  ['npo', 'nothing by mouth'],
  ['gdmt', 'guideline directed medical therapy'],
  ['dispo', 'disposition'],
  ['hpi', 'history of present illness'],
  ['ap', 'assessment and plan'],
];

// term -> the other terms in any group it belongs to.
const SYNONYMS: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const group of CONCEPT_GROUPS) {
    for (const term of group) {
      const key = term.toLowerCase();
      const others = group.filter((t) => t.toLowerCase() !== key);
      map.set(key, [...(map.get(key) ?? []), ...others]);
    }
  }
  return map;
})();

/** Synonyms/expansions for a single lower-case query token (empty if none). */
export function expandToken(token: string): string[] {
  return SYNONYMS.get(token.toLowerCase()) ?? [];
}
